import React, { useState, useEffect, useContext, useMemo } from "react";
import "./Style.css";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import { NavLink, useNavigate } from "react-router-dom";
import Modal from "./Modal";
import WeeklyScheduleModal from "./WeeklyScheduleModal";
import { Button } from "@mui/material";
import { NotificationContext } from "../redux/NotificationProvider";
import CollapsiblePanel from "./shared/CollapsiblePanel";
const envVariable = require("../EnvironmentVariables.json");

const Employees = () => {
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [sortField, setSortField] = useState("firstName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [roleFilter, setRoleFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const notify = useContext(NotificationContext);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchEmployees = async () => {
      const SC_Id = localStorage.getItem("SC_Id");
      try {
        const response = await fetch(envVariable.serverId + `/SCO/GetEmployees.php?SC_Id=${SC_Id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          const rawResponse = await response.text();
          console.error("Failed to fetch employees. Response:", rawResponse);
          return;
        }

        const data = await response.json();

        if (data.status === "Error") {
          console.error(data.message);
        } else if (data.status === "Success" && Array.isArray(data.employees)) {
          setEmployees(data.employees);
          setCurrentPage(1);
        } else {
          throw new Error("Unexpected response structure");
        }
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const roleOptions = useMemo(() => {
    const roles = employees
      .map((employee) => employee.role)
      .filter((role) => role && role.trim() !== "");
    return Array.from(new Set(roles)).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const query = (searchQuery || "").toLowerCase();
    const normalize = (value) => (value ? value.toString().toLowerCase() : "");

    const matchesSearch = (employee) => {
      const first = normalize(employee.firstName);
      const last = normalize(employee.lastName);
      const contact = normalize(employee.contact);
      const identifier = normalize(employee.id);
      return (
        first.includes(query) ||
        last.includes(query) ||
        contact.includes(query) ||
        identifier.includes(query)
      );
    };

    const getSortValue = (employee) => {
      switch (sortField) {
        case "lastName":
          return normalize(employee.lastName);
        case "role":
          return normalize(employee.role);
        case "firstName":
        default:
          return normalize(employee.firstName);
      }
    };

    const filtered = employees
      .filter((employee) => matchesSearch(employee))
      .filter((employee) => (roleFilter === "all" ? true : employee.role === roleFilter));

    const sorted = [...filtered].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (aValue === bValue) {
        return 0;
      }

      if (!aValue) {
        return 1;
      }

      if (!bValue) {
        return -1;
      }

      const comparison = aValue.localeCompare(bValue);

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [employees, searchQuery, roleFilter, sortField, sortOrder]);

  const handleSortFieldChange = (field) => {
    if (sortField === field) {
      // If clicking the same column, toggle the order
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // If clicking a new column, set it and default to ascending
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (employeeToDelete) {
      try {
        const response = await fetch(envVariable.serverId + "/SCO/DeleteEmployee.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: employeeToDelete.id }),
        });

        const data = await response.json();

        if (data.status === "Success") {
          setEmployees(employees.filter((employee) => employee.id !== employeeToDelete.id));
          setModalOpen(false);
        } else {
          notify("Failed to delete employee.","error");
        }
      } catch (error) {
        console.error("Error:", error);
        notify("There was an error deleting the employee.","error");
      }
    }
  };

  const handleViewUpdate = (employee) => {
    navigate(`/employees/update-employee/${employee.id}`, { state: { employee } });
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setModalOpen(true);
  };

  const handleScheduleClick = (employee) => {
    setSelectedEmployee(employee);
    setScheduleModalOpen(true);
  };

  const pageCount = Math.max(1, Math.ceil(filteredEmployees.length / itemsPerPage));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(pageCount, prev + 1));
  };

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const hasEmployees = filteredEmployees.length > 0;

  useEffect(() => {
    if (roleFilter !== "all" && !roleOptions.includes(roleFilter)) {
      setRoleFilter("all");
      setCurrentPage(1);
    }
  }, [roleFilter, roleOptions]);

  return (
    <>
      <MenuBar />
      <Modal
        isOpen={modalOpen}
        title={"Delete Employee"}
        dialog={"Confirm Employee Deletion: Are you sure you want to proceed with deleting this employee?"}
        showButtons={true}
        onClose={() => setModalOpen(false)}
        onConfirm={handleDelete}
      >
        <h2>Delete Employee</h2>
        <p>Are you sure you want to delete this employee?</p>
      </Modal>

      <WeeklyScheduleModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        mechanic={selectedEmployee}
        scId={localStorage.getItem("SC_Id")}
      />
      <div className="employees-container">
        <CollapsiblePanel id="employees-toolbar" title="Employee controls" className="sco-toolbar-collapsible">
          <div className="employees-search-section sco-toolbar">
            <input
              type="text"
              placeholder="Search for Employee"
              className="search-input"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <div className="sco-toolbar-actions">
              <NavLink to="/add-employee" className="add-button">
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    padding: "10px 20px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "17px",
                    textTransform: "none",
                  }}
                >
                  + Add New Employee
                </Button>
              </NavLink>
            </div>
          </div>
          <div className="sco-inline-toolbar">
            <div className="employees-control">
              <span className="employees-control-label">Filter</span>
              <select
                className="employees-select"
                value={roleFilter}
                onChange={(e) => handleRoleFilterChange(e.target.value)}
              >
                <option value="all">All Roles</option>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            <div className="pagination pagination-compact">
              <button
                onClick={goToPrevPage}
                className="page-item"
                disabled={currentPage === 1 || !hasEmployees}
              >
                {"<"}
              </button>
              <span className="pagination-status">
                Page {currentPage} / {pageCount}
              </span>
              <button
                onClick={goToNextPage}
                className="page-item"
                disabled={currentPage >= pageCount || !hasEmployees}
              >
                {">"}
              </button>
            </div>
          </div>
        </CollapsiblePanel>

        <table className="employees-table">
          <thead>
            <tr>
              <th onClick={() => handleSortFieldChange("firstName")} className="sortable-header">
                First Name {sortField === "firstName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("lastName")} className="sortable-header">
                Last Name {sortField === "lastName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Contact</th>
              <th onClick={() => handleSortFieldChange("role")} className="sortable-header">
                Role {sortField === "role" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.firstName}</td>
                <td>{employee.lastName}</td>
                <td>{employee.contact}</td>
                <td>{employee.role}</td>
                <td>
                  <button className="action-button view-update" onClick={() => handleViewUpdate(employee)}>
                    View / Update
                  </button>
                  <button
                    className="action-button"
                    style={{
                      backgroundColor: employee.role === 'Mechanic' ? '#4CAF50' : '#cccccc',
                      cursor: employee.role === 'Mechanic' ? 'pointer' : 'not-allowed',
                      opacity: employee.role === 'Mechanic' ? 1 : 0.6
                    }}
                    onClick={() => employee.role === 'Mechanic' && handleScheduleClick(employee)}
                    disabled={employee.role !== 'Mechanic'}
                    title={employee.role !== 'Mechanic' ? 'Weekly schedule is only available for mechanics' : ''}
                  >
                    Weekly Schedule
                  </button>
                  <button className="action-button delete" onClick={() => handleDeleteClick(employee)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination at the bottom */}
        <div className="pagination pagination-compact" style={{ justifyContent: "center", marginTop: "20px" }}>
          <button
            onClick={goToPrevPage}
            className="page-item"
            disabled={currentPage === 1 || !hasEmployees}
          >
            {"<"}
          </button>
          <span className="pagination-status">
            Page {currentPage} / {pageCount}
          </span>
          <button
            onClick={goToNextPage}
            className="page-item"
            disabled={currentPage >= pageCount || !hasEmployees}
          >
            {">"}
          </button>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Employees;
